import { Instagram, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollToSection } from "@/lib/scrollToSection";

const CATERING_URL = "https://4sisterskitchen.com/";
const logoSrc = "/assets/logo/logo-168.png";
const logoPngSrcSet = "/assets/logo/logo-168.png 168w, /assets/logo/logo-336.png 336w";
const logoWebpSrcSet = "/assets/logo/logo-168.webp 168w, /assets/logo/logo-336.webp 336w";

export function Footer() {
  const { t } = useTranslation();

  const quickLinks = [
    { key: "home", name: t("nav.home"), href: "#hero" },
    { key: "services", name: t("nav.services"), href: "#services" },
    { key: "portfolio", name: t("nav.portfolio"), href: "#portfolio" },
    { key: "team", name: t("nav.team"), href: "#team" },
    { key: "catering", name: t("footer.cateringLink"), href: CATERING_URL, external: true },
    { key: "contact", name: t("nav.contact"), href: "#contact" }
  ];

  const services = [
    { key: "corporate", name: t("services.corporate.title"), href: "#" },
    { key: "private", name: t("services.private.title"), href: "#" },
    { key: "weddings", name: t("services.weddings.title"), href: "#" },
    { key: "planning", name: t("services.planning.title"), href: "#" },
    { key: "festivals", name: t("services.festivals.title"), href: "#" },
    { key: "concerts", name: t("services.concerts.title"), href: "#" }
  ];

  const socialLinks = [
    { icon: Instagram, href: "https://www.instagram.com/4sisters_events/", name: "Instagram" }
  ];

  return (
    <footer className="bg-gray-900 text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div data-testid="footer-company-info">
            <picture>
              <source srcSet={logoWebpSrcSet} sizes="168px" type="image/webp" />
              <img 
                src={logoSrc}
                srcSet={logoPngSrcSet}
                sizes="168px"
                alt="4Sisters Events" 
                className="h-14 w-auto mb-4" 
                width="168"
                height="56"
                loading="lazy"
                decoding="async"
                data-testid="footer-logo"
              />
            </picture>
            <p className="text-gray-400 mb-6">
              {t("footer.description")}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a 
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-primary transition-colors duration-200"
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span className="sr-only">{social.name}</span>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div data-testid="footer-quick-links">
            <h3 className="font-semibold text-lg mb-4">{t("footer.quickLinksTitle")}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  {link.external ? (
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-primary transition-colors duration-200"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-gray-400 hover:text-primary transition-colors duration-200 text-left"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div data-testid="footer-services">
            <h3 className="font-semibold text-lg mb-4">{t("footer.servicesTitle")}</h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.key}>
                  <a 
                    href={service.href} 
                    className="text-gray-400 hover:text-primary transition-colors duration-200"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div data-testid="footer-contact-info">
            <h3 className="font-semibold text-lg mb-4">{t("footer.contactTitle")}</h3>
            <div className="space-y-3">
              <div className="flex items-center" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-primary mr-3" />
                <span className="text-gray-400">+372 51 92 3590</span>
              </div>
              <div className="flex items-center" data-testid="footer-email">
                <svg className="w-4 h-4 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-400">kristin@4sistersevents.ee</span>
              </div>
              <div className="flex items-center" data-testid="footer-hours">
                <svg className="w-4 h-4 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12,6 12,12 16,14"></polyline>
                </svg>
                <span className="text-gray-400">{t("footer.hours")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-12 pt-8" data-testid="footer-bottom">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0" data-testid="footer-copyright">
              {t("footer.copyright")}
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-primary transition-colors duration-200" data-testid="footer-privacy">
                {t("footer.privacy")}
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors duration-200" data-testid="footer-terms">
                {t("footer.terms")}
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors duration-200" data-testid="footer-cookies">
                {t("footer.cookies")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
